import axios from "axios";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { APP_BASE_URL, APP_BASE_URL_SOCKET, GET_LATEST_TELEMETRY } from "../../Common/EndPoints";
import { selectedCustomersIdsSelector, userIdSelector } from "../../features/authentication/auth-slice";
import { getLocalStorage } from "../../Utils/storage-utils";
import toast from "react-hot-toast";

function filterAndParseTelemetryData(response: any[], required_device_id_list?: string[]) {
  const raw_data_map: Record<any, any> = {};
  for (const data of response) {
    const _device_id = data.deviceId;
    if (!required_device_id_list?.includes(_device_id)) continue;
    raw_data_map[_device_id] = { point_machine_subgear: null };

    for (const packet of data.dataLists) {
      const data = packet.data;
      raw_data_map[_device_id] = { ...raw_data_map[_device_id], ...data };
    }
    if (!raw_data_map[_device_id].point_machine_subgear) delete raw_data_map[_device_id].point_machine_subgear;
  }
  return raw_data_map;
}

type GetLatestTelemetryRequest = {
  body: { customerIds: string[]; status: string; deviceCategory?: string | null };
  signal?: AbortSignal;
};
function getLatestTelemetry({ body, signal }: GetLatestTelemetryRequest) {
  return axios.post(APP_BASE_URL + GET_LATEST_TELEMETRY, body, { signal });
}

type Params = {
  required_device_id_list?: string[];
  deviceCategory?: string;
  skip?: boolean;
};
export function useGetTelemetryData({ required_device_id_list, deviceCategory, skip = false }: Params) {
  const customerIdList = useSelector(selectedCustomersIdsSelector) as string[];
  const userId = useSelector(userIdSelector);
  const telemetry_websocket = useRef<undefined | WebSocket>();
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const dataRef = useRef<any>({});

  const getLiveData = useCallback(() => {
    if (telemetry_websocket.current) {
      telemetry_websocket.current.close();
    }
    let url = `${APP_BASE_URL_SOCKET}/${userId}`;
    const secret_key = getLocalStorage("id_key") ?? "";

    if (secret_key) {
      url = `${url}/key=${secret_key}`;
    }
    telemetry_websocket.current = new WebSocket(url);
    telemetry_websocket.current.onmessage = (res) => {
      if (!res?.data) return;
      let data = JSON.parse(res.data);
      const device_id_of_received_packet = data.deviceId;
      const packet_data = data?.dataLists?.[0].data;
      if (!dataRef.current || !dataRef.current[device_id_of_received_packet]) return;
      // console.log("dataRef", device_id_of_received_packet, dataRef);
      //   console.log("device_id_is_present");

      dataRef.current[device_id_of_received_packet] = {
        ...dataRef.current[device_id_of_received_packet],
        ...packet_data,
      };

      setData({ ...dataRef.current });
    };

    telemetry_websocket.current.onclose = (error) => {
      if (error?.code === 1008) {
        toast.error(error?.reason || "Websocket Unautherized");
      }
    };
  }, [userId]);

  const populateTelemetryData = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      let body = {
        customerIds: customerIdList,
        status: "ALL",
        deviceCategory,
      };

      try {
        const raw_response = await getLatestTelemetry({ body, signal });
        const response = raw_response.data.data;
        const parsed_data = filterAndParseTelemetryData(response, required_device_id_list);
        // console.log("parsed_data_using_api", parsed_data);
        dataRef.current = parsed_data;
        getLiveData();
        setData(parsed_data);
        setLoading(false);
      } catch (error) {}
    },
    [customerIdList, required_device_id_list, deviceCategory, getLiveData],
  );

  useEffect(() => {
    if (skip) return;
    const controller = new AbortController();
    const signal = controller.signal;
    if (required_device_id_list?.length === 0) return;
    populateTelemetryData(signal);
    return () => {
      controller.abort();
      telemetry_websocket?.current?.close?.();
    };
  }, [required_device_id_list, populateTelemetryData, skip]);

  return useMemo(() => ({ data, loading }), [data, loading]);
}
